import { DatePipe } from '@angular/common'
import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core'
import { FormControl, UntypedFormBuilder } from '@angular/forms'
import { ActiveCartService } from '@spartacus/cart/base/core'
import { CheckoutPlaceOrderComponent } from '@spartacus/checkout/base/components'
import { GlobalMessageType, RoutingService } from '@spartacus/core'
import { OrderFacade } from '@spartacus/order/root'
import { LaunchDialogService } from '@spartacus/storefront'
import { DateTime } from 'luxon'
import { map, Observable, Subscription } from 'rxjs'
import { BaseCountry } from '../../../models/enum/country.enum'
import { CustomCartService } from '../../../services/custom-cart.service'
import { UserCutoffTime, WeekDay } from '../../../services/shared/calendar/calendar.model'
import { CalendarService } from '../../../services/shared/calendar/calendar.service'
import { CustomGlobalMessageService } from '../../../services/shared/custom-global-message/custom-global-message.service'
import { SharedService } from '../../../services/shared/shared.service'
import { UserPermissionService } from '../../../services/user-permission/user-permission.service'

@Component({
  selector: 'app-custom-place-order',
  templateUrl: './custom-place-order.component.html',
  styleUrls: ['./custom-place-order.component.scss'],
  providers: [],
  standalone: false
})
export class CustomPlaceOrderComponent extends CheckoutPlaceOrderComponent implements OnInit, OnDestroy {
  todayDate: Date
  minDate: Date
  monthChanged: boolean = false
  public holidayDates: string[]
  public activeCartId: string
  totalAmount: any
  userMinimumSaleOrderAmount: any
  userCutOff: UserCutoffTime
  daysOfWeekNotChecked$: Observable<any>
  private defaultCutoffTime = '12:00:00 PM'
  private hasCutoffTimeRecord = false
  subscription: Subscription = new Subscription()

  private cutoffDateTimeInMelbourne: string
  private readonly timezoneToIANAtimezone = new Map<string, string>([
    ['AUSACT', 'Australia/Sydney'],
    ['AUSNSW', 'Australia/Sydney'],
    ['AUSNT', 'Australia/Darwin'],
    ['AUSQLD', 'Australia/Brisbane'],
    ['AUSSA', 'Australia/Adelaide'],
    ['AUSTAS', 'Australia/Hobart'],
    ['AUSVIC', 'Australia/Melbourne'],
    ['AUSWA', 'Australia/Perth'],
    ['NZCHA', 'Pacific/Chatham'],
    ['NZST', 'Pacific/Auckland']
  ])

  constructor(
    protected orderFacade: OrderFacade,
    protected routingService: RoutingService,
    protected fb: UntypedFormBuilder,
    protected launchDialogService: LaunchDialogService,
    protected vcr: ViewContainerRef,
    public sharedService: SharedService,
    private activeCartService: ActiveCartService,
    private customCartService: CustomCartService,
    public globalMessageService: CustomGlobalMessageService,
    public datePipe: DatePipe,
    public userPermissionService: UserPermissionService,
    private calendarService: CalendarService
  ) {
    super(orderFacade, routingService, fb, launchDialogService, vcr)
  }

  public ngOnInit(): void {
    this.checkoutSubmitForm.addControl('specialInstructions', new FormControl('', null))
    this.checkoutSubmitForm.addControl('requestedDeliveryDate', new FormControl('', null))
    this.checkoutSubmitForm.addControl('totalPrice', new FormControl('', null))

    this.setActiveCartId()

    this.userPermissionService
      .getUserMinimumSaleOrderAmount()
      .subscribe((res) => (this.userMinimumSaleOrderAmount = res))

    this.convertCutOffTimeToMelbourneTimezone()
    this.setHolidays()
  }

  // #region set cutOff datetime
  private convertCutOffTimeToMelbourneTimezone(): void {
    const userCutoffTime$ = this.calendarService.getCutOffTime().pipe(
      map((cutoffTime) => {
        //If object empty, the user has no record in cut off time table
        this.hasCutoffTimeRecord = Object.keys(cutoffTime).length > 0
        this.userCutOff = {
          additionaDays: isNaN(Number(cutoffTime?.additionaDays)) ? 0 : Number(cutoffTime?.additionaDays),
          beforeAdditionalDays: isNaN(Number(cutoffTime?.beforeAdditionalDays))
            ? 0
            : Number(cutoffTime?.beforeAdditionalDays),
          cutOffTime: cutoffTime?.cutOffTime ?? this.defaultCutoffTime, // Default to Melbourne cutoff time (12:00:00) if no cutoff is provided
          timeZone: cutoffTime?.timeZone ?? this.defaultTimeZoneForCurrentUser, // Use the user's time zone if provided; otherwise, use the default time zone for the user's country.
          allowedDeliveryWeekDays: cutoffTime.weekDays ?? null
        }

        // Use the user's time zone if found; otherwise, use the default time zone for the user's country.
        const sourceTimeZone =
          this.timezoneToIANAtimezone.get(this.userCutOff.timeZone) ?? this.defaultTimeZoneForCurrentUser
        const targetTimeZone = this.timezoneToIANAtimezone.get('AUSVIC')
        const today = DateTime.now().toFormat('yyyy-MM-dd')
        const dateTimeString = `${today} ${this.userCutOff.cutOffTime}`

        const formattedMelbourne = this.convertToFormattedTimeZone(dateTimeString, sourceTimeZone, targetTimeZone)
        return formattedMelbourne
      })
    )

    this.subscription.add(
      userCutoffTime$.subscribe((res: string) => {
        this.cutoffDateTimeInMelbourne = res
      })
    )
  }

  private get defaultTimeZoneForCurrentUser(): string {
    return this.isAUCountry ? this.timezoneToIANAtimezone.get('AUSVIC') : this.timezoneToIANAtimezone.get('NZST')
  }

  /**
   * Converts a time string from one time zone to another and returns a formatted string.
   * @param dateTimeString - Date and time string (e.g. '2025-10-17 11:00:00 AM')
   * @param sourceTimeZone - IANA time zone of the input time (e.g. 'Pacific/Chatham')
   * @param targetTimeZone - IANA time zone to convert to (e.g. 'Australia/Melbourne')
   * @returns Formatted date-time string in target time zone (e.g. '2025-10-17 08:15:00+11:00')
   */
  private convertToFormattedTimeZone(dateTimeString: string, sourceTimeZone: string, targetTimeZone: string): string {
    const sourceDateTime = DateTime.fromFormat(dateTimeString, 'yyyy-MM-dd hh:mm:ss a', {
      zone: sourceTimeZone
    })

    // Convert to target time zone - 'Australia/Melbourne'
    const targetDateTime = sourceDateTime.setZone(targetTimeZone)

    // Format the result
    return targetDateTime.toFormat('yyyy-MM-dd HH:mm:ssZZ')
  }

  /**
   * Check if current datetime in Melbourne is bigger than cutoff datetime in Melbourne timezone
   */
  private get isCurrentDateTimeBiggerThanCutOff(): boolean {
    const currentMelbourneTime = DateTime.now().setZone('Australia/Melbourne')

    const cutoffTime = DateTime.fromFormat(this.cutoffDateTimeInMelbourne, 'yyyy-MM-dd HH:mm:ssZZ', {
      zone: 'Australia/Melbourne'
    })

    return currentMelbourneTime > cutoffTime
  }

  private get isAUCountry(): boolean {
    return this.sharedService.baseSiteId == BaseCountry.AU
  }

  /**
   * current time (in Melbourne) after cutoff time (in Melbourne) and AU country -> add 2 days to delivery min date
   * current time (in Melbourne) after cutoff time (in Melbourne) and NZ country -> add 2 days to delivery min date
   * current time (in Melbourne) before cutoff time (in Melbourne) and AU country -> add 2 days to delivery min date
   * current time (in Melbourne) before cutoff time (in Melbourne) and NZ country -> add 2 days to delivery min date
   *
   * Exception:
   * If there is no cutofftime record found
   * After the default cutofftime, for AU the default day should be 3, and for NZ it should be 2
   * No additonal days added
   */
  private get minDaysCutOff(): number {
    switch (this.isCurrentDateTimeBiggerThanCutOff) {
      case true: {
        if (!this.hasCutoffTimeRecord) {
          return this.isAUCountry ? 3 : 2
        } else {
          return this.isAUCountry ? 2 + this.userCutOff.additionaDays : 2 + this.userCutOff.additionaDays
        }
      }
      case false:
        return this.isAUCountry ? 2 + this.userCutOff.beforeAdditionalDays : 2 + this.userCutOff.beforeAdditionalDays
    }
  }

  isAllowedDeliveryWeekDay(date: Date): boolean {
    if (!this.userCutOff.allowedDeliveryWeekDays) {
      return true
    } else {
      const todayWeekDay = this.getWeekday(date.getDay())
      return this.userCutOff.allowedDeliveryWeekDays.includes(todayWeekDay)
    }
  }

  private getWeekday(dayNumber: WeekDay): string {
    return WeekDay[dayNumber]
  }

  //#endregion set cutoff datetime

  submitForm(): void {
    if (this.sharedService.isUserCreditHold) {
      this.sharedService.showCreditHoldMessage()
      return
    }

    this.activeCartService.getActive().subscribe((res) => {
      if (res.totalPriceWithTax) {
        this.totalAmount = res.totalPriceWithTax.value
      }
    })

    if (this.userMinimumSaleOrderAmount != '' && this.totalAmount < this.userMinimumSaleOrderAmount) {
      this.sharedService.showTotalAmountLessMessage(this.userMinimumSaleOrderAmount)
      return
    }

    let specialInstructions = this.checkoutSubmitForm.controls.specialInstructions.value
    if (!specialInstructions?.length) {
      specialInstructions = 'None'
    } else {
      specialInstructions = specialInstructions.trim() || 'None'
    }

    const selectedDate = this.checkoutSubmitForm.controls.requestedDeliveryDate.value
    let requestedDeliveryDate = this.datePipe.transform(selectedDate, 'dd-MM-yyyy')
    if (!selectedDate) {
      requestedDeliveryDate = null
    }
    this.customCartService
      .AddSpecialInstructions(this.activeCartId, specialInstructions, requestedDeliveryDate)
      .subscribe(
        () => {
          super.submitForm()
        },
        (err) => {
          this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
        }
      )
  }

  get disableOnCreditHoldOrInvalidTerms(): boolean {
    return super.termsAndConditionInvalid || this.sharedService.isUserCreditHold
  }

  public setHolidays() {
    this.calendarService.getHolidays().subscribe(
      (dates: string[]) => {
        if (dates && dates.length > 0) {
          this.holidayDates = dates
        }
      },
      (err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      }
    )
  }

  public setActiveCartId(): void {
    this.activeCartService
      .getActiveCartId()
      .subscribe((res) => {
        this.activeCartId = res
      })
      .unsubscribe()
  }

  public openDatePicker(): void {
    this.todayDate = new Date()
    this.minDate = this.calculateMinDate(this.todayDate, this.minDaysCutOff)
  }

  // Calculate the minimum date excluding weekedends and Australian Holidays
  private calculateMinDate(date: Date, minDaysCutOff: number): Date {
    const minDate = new Date(date)
    let count = 0

    while (count < minDaysCutOff) {
      minDate.setDate(minDate.getDate() + 1)
      // Check if the new date is a weekday
      if (minDate.getDay() !== 0 && minDate.getDay() !== 6 && !this.isHoliday(minDate)) {
        count++
      }
    }

    return minDate
  }

  isHoliday(date: Date): boolean {
    return this.holidayDates
      ? !!this.holidayDates.find((holidayDate) => holidayDate === this.datePipe.transform(date, 'dd-MM-yyyy'))
      : false
  }

  holidaysAndDeliverydaysFilter = (datepickerDate: Date): boolean => {
    const date = datepickerDate
    if (date) {
      // month changed in calender manually
      if (this.todayDate.getMonth() != date.getMonth()) {
        this.monthChanged = true
      } else if (this.monthChanged && this.todayDate.getMonth() == date.getMonth()) {
        this.monthChanged = false
      }

      // previous dates
      if (this.todayDate > date) {
        return false
      }

      let isWeekend = false // weekend
      const isHoliday = this.isHoliday(date) //holidays
      const isAllowedDeliveryWeekDay = this.isAllowedDeliveryWeekDay(date) //delivery days

      const day = date.getDay()

      // weekend
      if (day == 0 || day == 6) {
        isWeekend = true
      }

      if (this.isAUCountry) {
        return this.disableDate(isHoliday, isWeekend, isAllowedDeliveryWeekDay)
      }
      if (isHoliday || isWeekend || !isAllowedDeliveryWeekDay) {
        return false
      }
    }
    return true
  }

  private disableDate(isHoliday, isWeekend, isAllowedDeliveryWeekDay): boolean {
    if (isHoliday || isWeekend || !isAllowedDeliveryWeekDay) {
      return false
    }

    return true
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    this.subscription.unsubscribe()
  }

  public getTermsAndConditions(e): void {
    e.preventDefault()

    this.customCartService.downloadTermsAndConditions().subscribe((pdfUrl) => {
      window.open(pdfUrl, '_blank')
    })
  }
}
